/* Surisa mobile drawer V2 — slide-in 80% desde la izquierda con dos niveles.
   Acompaña a snippets/header-drawer.liquid + assets/component-menu-drawer.css. */

class MenuDrawerV2 extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.activeLevel = 0;
    this.activeHandle = null;
  }

  connectedCallback() {
    this.trigger      = this.querySelector('.menu-drawer-v2__trigger');
    this.panel        = this.querySelector('.menu-drawer-v2__panel');
    this.backdrop     = this.querySelector('.menu-drawer-v2__backdrop');
    this.level0       = this.querySelector('[data-level="0"]');
    this.levels1      = this.querySelectorAll('[data-level="1"]');

    if (!this.trigger || !this.panel) return;

    this.onKeydown = this.onKeydown.bind(this);

    this.trigger.addEventListener('click', () => this.open());
    this.backdrop.addEventListener('click', () => this.close());

    /* Query buttons inside the panel BEFORE we move it to <body>, then
       attach listeners on those cached references. The listeners persist
       through the DOM move. */
    this.panel.querySelectorAll('[data-drawer-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });
    this.panel.querySelectorAll('[data-drawer-back]').forEach((btn) => {
      btn.addEventListener('click', () => this.showLevel0());
    });
    this.panel.querySelectorAll('[data-drawer-go-to]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.showLevel1(btn.dataset.drawerGoTo, btn.dataset.categoryLabel);
      });
    });
    this.panel.querySelectorAll('[data-drawer-tab]').forEach((tab) => {
      tab.addEventListener('click', () => this.switchTab(tab));
    });

    /* Portal: relocate backdrop + panel to <body> so they escape any
       stacking context created by the sticky header. Without this, the
       drawer's high z-index is bounded inside the header (z-index:4) and
       loses against unrelated fixed bars at root level (e.g. the mobile
       sticky CTAs at z-index:50). */
    document.body.appendChild(this.backdrop);
    document.body.appendChild(this.panel);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    /* Clean up portaled nodes if the custom element gets torn down. */
    if (this.backdrop && this.backdrop.parentNode === document.body) {
      document.body.removeChild(this.backdrop);
    }
    if (this.panel && this.panel.parentNode === document.body) {
      document.body.removeChild(this.panel);
    }
  }

  open() {
    this.isOpen = true;
    this.panel.hidden = false;
    this.backdrop.hidden = false;
    /* allow next paint to commit hidden=false before applying transition class */
    requestAnimationFrame(() => {
      this.classList.add('is-open');
      /* Panel + backdrop live in <body> after the portal, so they can't
         rely on a descendant selector from the custom element — apply the
         class directly to drive their transitions. */
      this.panel.classList.add('is-open');
      this.backdrop.classList.add('is-open');
      this.panel.setAttribute('aria-hidden', 'false');
      this.trigger.setAttribute('aria-expanded', 'true');
    });
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', this.onKeydown);

    /* Dawn utilities for focus management */
    if (typeof trapFocus === 'function') trapFocus(this.panel);
  }

  close() {
    this.isOpen = false;
    this.classList.remove('is-open');
    this.panel.classList.remove('is-open');
    this.backdrop.classList.remove('is-open');
    this.panel.setAttribute('aria-hidden', 'true');
    this.trigger.setAttribute('aria-expanded', 'false');

    /* wait for transition to end before hiding */
    setTimeout(() => {
      if (!this.isOpen) {
        this.panel.hidden = true;
        this.backdrop.hidden = true;
        this.showLevel0(false);
      }
    }, 260);

    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', this.onKeydown);

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.trigger);
    } else {
      this.trigger.focus();
    }
  }

  showLevel0(focusReset = true) {
    this.activeLevel = 0;
    this.activeHandle = null;
    this.classList.remove('is-level-1');
    this.level0.hidden = false;
    this.levels1.forEach((l) => { l.hidden = true; });
    if (focusReset && this.isOpen && typeof trapFocus === 'function') {
      trapFocus(this.level0);
    }
  }

  showLevel1(handle, label) {
    const target = Array.from(this.levels1).find((l) => l.dataset.categoryHandle === handle);
    if (!target) return;
    this.activeLevel = 1;
    this.activeHandle = handle;
    this.level0.hidden = true;
    this.levels1.forEach((l) => { l.hidden = (l !== target); });
    /* update the visible title with the link's label (T&A-translated) */
    if (label) {
      const nameEl = target.querySelector('[data-category-name]');
      if (nameEl) nameEl.textContent = label;
    }
    /* reset to the Productos tab when entering a new category */
    const firstTab = target.querySelector('[data-drawer-tab="productos"]');
    if (firstTab) this.switchTab(firstTab);
    this.classList.add('is-level-1');
    if (typeof trapFocus === 'function') trapFocus(target);
  }

  switchTab(tabBtn) {
    const level1 = tabBtn.closest('[data-level="1"]');
    if (!level1) return;
    const id = tabBtn.dataset.drawerTab;
    level1.querySelectorAll('[data-drawer-tab]').forEach((t) => {
      const active = t === tabBtn;
      t.classList.toggle('is-active', active);
      t.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    level1.querySelectorAll('[data-drawer-panel]').forEach((p) => {
      p.hidden = (p.dataset.drawerPanel !== id);
    });
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      if (this.activeLevel === 1) this.showLevel0();
      else this.close();
    }
  }
}

customElements.define('menu-drawer-v2', MenuDrawerV2);
